// Thin React wrapper around the canvas force-sim renderer. The renderer
// owns the imperative animation loop + pointer events; React just mounts/
// unmounts it, syncs declarative props (heat overlay, selection, etc.)
// onto the renderer, and forwards selection/hover callbacks back up.

import { useEffect, useRef } from 'react';

import { ForceSim2D, type IGraphEdge, type IGraphNode } from './force-sim';
import { GraphRenderer2D } from './graph-renderer';

interface IGraphCanvasProps {
  nodes: IGraphNode[];
  edges: IGraphEdge[];
  participatingIds: Set<string>;
  heatEnabled: boolean;
  heatScores: Map<string, number>;
  edgeHeatScores: Map<string, number>;
  heatGamma: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
}

export function GraphCanvas(props: IGraphCanvasProps) {
  const {
    nodes,
    edges,
    participatingIds,
    heatEnabled,
    heatScores,
    edgeHeatScores,
    heatGamma,
    selectedId,
    onSelect,
    onHover,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<GraphRenderer2D | null>(null);

  // Stable refs for the latest callbacks so we don't need to rebuild the
  // renderer when consumers pass inline handlers.
  const onSelectRef = useRef(onSelect);
  const onHoverRef = useRef(onHover);
  onSelectRef.current = onSelect;
  onHoverRef.current = onHover;

  // Build (or rebuild) the sim + renderer whenever the graph topology
  // changes. Identity comparison on nodes/edges is intentional — callers
  // reset the array references when versioning or pipeline changes.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;

    const w = container.clientWidth;
    const h = container.clientHeight;
    const sim = new ForceSim2D(nodes, edges, w, h);
    const renderer = new GraphRenderer2D(canvas, sim, edges);
    renderer.resize(w, h);
    renderer.onSelect = (id) => onSelectRef.current(id);
    renderer.onHover = (id) => onHoverRef.current(id);
    rendererRef.current = renderer;
    renderer.start();

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      renderer.resize(width, height);
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      renderer.stop();
      rendererRef.current = null;
    };
  }, [nodes, edges]);

  // Sync declarative overlay state onto the imperative renderer.
  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    r.heatEnabled = heatEnabled;
    r.heatScores = heatScores;
    r.edgeHeatScores = edgeHeatScores;
    r.heatGamma = heatGamma;
  }, [heatEnabled, heatScores, edgeHeatScores, heatGamma]);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    r.participatingIds = participatingIds;
  }, [participatingIds]);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    r.selectedId = selectedId;
  }, [selectedId]);

  return (
    <div ref={containerRef} className="kg-stage" style={{ position: 'absolute', inset: 0 }}>
      <canvas ref={canvasRef} />
    </div>
  );
}
